import { isSystemReminderPart } from '@/components/chat-v2/lib/contentConverters'

import type { ChatMessage, RunIdToMsgIndex } from '@/types/chat-v2'

const userPromptContentKeys = (message: ChatMessage): Set<string> => {
  if (message.message.kind !== 'request') return new Set()

  const keys = new Set<string>()
  for (const part of message.message.parts) {
    if (part.part_kind !== 'user-prompt' || isSystemReminderPart(part)) continue
    const key = JSON.stringify(part.content)
    if (key !== undefined) keys.add(key)
  }
  return keys
}

const hasSharedUserPromptContent = (
  optimisticMessage: ChatMessage,
  authoritativeMessage: ChatMessage,
): boolean => {
  const optimisticKeys = userPromptContentKeys(optimisticMessage)
  if (optimisticKeys.size === 0) return false

  const authoritativeKeys = userPromptContentKeys(authoritativeMessage)
  for (const key of optimisticKeys) {
    if (authoritativeKeys.has(key)) return true
  }
  return false
}

/** Whether a message carries any user-prompt content (vs e.g. a synthetic
 * tool-return request, which is request-kind but has no user-prompt part). */
export const hasUserPromptContent = (message: ChatMessage): boolean =>
  userPromptContentKeys(message).size > 0

export const findDuplicateOptimisticUserMessageIds = (
  messages: readonly ChatMessage[],
  authoritativeMessage: ChatMessage,
): Set<RunIdToMsgIndex> => {
  const duplicateIds = new Set<RunIdToMsgIndex>()
  for (const message of messages) {
    if (!message.is_optimistic || message.id === authoritativeMessage.id) {
      continue
    }
    if (message.index_in_thread !== authoritativeMessage.index_in_thread) {
      continue
    }
    if (hasSharedUserPromptContent(message, authoritativeMessage)) {
      duplicateIds.add(message.id)
    }
  }
  return duplicateIds
}

/**
 * The pending optimistic user message a drifted authoritative confirm
 * replaces. Exact-index matching (findDuplicateOptimisticUserMessageIds)
 * misses when local and server thread indexes disagree — e.g. a cancelled
 * run's partial assistant message still occupies a local slot the server
 * discarded, so a send right after Stop sits one index past its confirm.
 * Sends confirm in FIFO order, so the earliest content-matching pending
 * optimistic message is the one this confirm belongs to; at most one id is
 * returned so a repeated identical prompt keeps its own pending bubble.
 */
export const findConfirmedOptimisticUserMessageId = (
  messages: readonly ChatMessage[],
  authoritativeMessage: ChatMessage,
): RunIdToMsgIndex | null =>
  messages.find(
    (message) =>
      message.is_optimistic &&
      message.id !== authoritativeMessage.id &&
      hasSharedUserPromptContent(message, authoritativeMessage),
  )?.id ?? null
